// Disabling this rule because this is library code. We use unions to make sure
// our interfaces are correct, but we don't want to have to use type assertions
// everywhere when the type system makes them redundant.
/* eslint-disable @typescript-eslint/consistent-type-assertions */

import React from "react";

import cx from "classnames";

import { coalesce, setStateValue } from "@kazoohr/helpers";

import { Button } from "../Button";
import { ControlWrapper } from "../ControlWrapper";
import { Icon } from "../Icon";
import { IconDictionary } from "../Icon/types";

import styles from "./Input.scss";
import { RawInput } from "./RawInput";
/**
 * Base props shared by all input types
 */
interface BaseInputProps {
  /**
   * Input type. For `tel`, values are normalized toward E.164 (`+` allowed; up to 15 digits) unless `mask` is set.
   * @default 'text'
   */
  type?: "text" | "password" | "email" | "number" | "tel" | undefined;

  /**
   * Whether the input should be focused when it mounts.
   * @default false
   */
  autoFocus?: boolean;

  /**
   * Boolean to use AutoSave tooltip
   * @default false
   */
  autoSave?: boolean;

  /**
   * Optional string to be used in autoSave tooltip.
   * @default "SAVED"
   */
  savedLabel?: string;

  /**
   * Error string to display and applies error styling
   */
  error?: string;

  /**
   * Helper text to display beneath input.
   */
  helperText?: React.ReactNode;

  /**
   * Label text/node
   */
  label?: React.ReactNode;

  /**
   * String or function to mask this input with.
   * @see https://github.com/text-mask/text-mask/blob/master/componentDocumentation.md#mask
   */
  mask?: Array<string | RegExp> | ((value: string) => Array<string | RegExp>);

  /**
   * When masking, the default string to use as a placeholder.
   * @default '_'
   */
  placeholderChar?: string;

  /**
   * Text that will be displayed in the input label when required is false.
   */
  optionalLabelText?: string;

  /**
   * Displays the maxLength opposite to the input label
   */
  maxLengthAbove?: boolean;

  /**
   * Displays the maxLength character count
   * @default true
   */
  maxLengthText?: boolean;

  /**
   * Icon to display inside the input
   */
  icon?: IconDictionary;

  /**
   * Position where the icon will be displayed.
   * @default 'left'
   */
  iconPosition?: "left" | "right";

  /**
   * Sets the color of the icon.
   * @default '#949494'
   */
  iconColor?: string;

  /**
   * Size of the icon.
   * @default 16
   */
  iconSize?: number;

  /**
   * Sets the input to be numeric (removes non-numeric characters)
   */
  numeric?: boolean;

  /**
   * Hints the virtual keyboard mode on touch devices. Pass `"numeric"` to
   * show a digit-only keyboard on iOS/Android without changing input semantics.
   */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];

  /**
   * Sets text to the right side of input (requires numeric is true)
   */
  numericText?: string;

  /**
   * Applies compact styling
   */
  compact?: boolean;

  /**
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Hides the border of the input
   */
  inputOnly?: boolean;

  /**
   * Input tooltip
   */
  tooltip?: React.ReactNode;

  /**
   * Input identifier
   */
  id: string;

  /**
   * Runs when a key is pressed down while the input is focused.
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  /**
   * Autocomplete attribute for the input, if applicable.
   */
  autoComplete?: string;

  /**
   * Style for the input
   */
  style?: React.CSSProperties;

  /**
   * Render fixed text on the right side of the input.
   * Wraps the input in a div that looks like an input.
   */
  rightSideText?: string;

  /**
   * Whether the input is disabled
   */
  disabled?: boolean;

  /**
   * Input class name
   */
  className?: string;

  /**
   * Input required
   */
  required?: boolean;

  /**
   * Input onBlur handler
   */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /**
   * Input placeholder
   */
  placeholder?: string;

  /**
   * Maximum length of the input value
   */
  maxLength?: number;

  /**
   * Input onWheel handler
   */
  onWheel?: (event: React.WheelEvent<HTMLInputElement>) => void;

  /**
   * Name of the input
   */
  name?: string;

  /**
   * Show a button to reveal password while pressed (only applies to password type inputs)
   * @default false
   */
  showPasswordButton?: boolean;

  [key: string]: unknown;
}

interface TextInputProps extends BaseInputProps {
  type?: "text" | "email" | "password" | "tel" | undefined;
  value?: string;
  onChange: (value: string) => void;
  defaultValue?: string;
  /**
   * Labels for the password toggle button
   */
  passwordToggleLabels?: {
    show?: string;
    hide?: string;
  };
}

interface NumberInputProps extends BaseInputProps {
  type: "number";
  value?: number;
  onChange: (value: number) => void;
  defaultValue?: number;
  /**
   * Minimum value for the input
   */
  min?: number;
  /**
   * Maximum value for the input
   */
  max?: number;
  /**
   * Step value for the input
   */
  step?: number;
  /**
   * Removes non-numbers, and replaces leading zero with first number.
   * @default false
   */
  removeNonNumCharacters?: boolean;
}

export type InputProps = TextInputProps | NumberInputProps;

/**
 * Input component that provides a styled input with various features like masking,
 * icons, error states, and helper text.
 */
// eslint-disable-next-line max-lines-per-function
export function Input({
  autoSave = false,
  savedLabel = "SAVED",
  onBlur,
  disabled,
  error,
  fullWidth,
  helperText,
  id,
  label,
  required,
  optionalLabelText,
  maxLengthAbove,
  maxLengthText,
  numeric,
  numericText,
  icon,
  iconPosition = "left",
  iconColor = "#949494",
  iconSize = 16,
  className,
  tooltip,
  rightSideText,
  type,
  value,
  onChange,
  showPasswordButton = false,
  passwordToggleLabels,
  ...inputProps
}: InputProps): JSX.Element {
  const [saved, setSaved] = React.useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      saved ? setSaved(false) : null;
    }, 2000);
    return () => clearTimeout(timeout);
  }, [saved]);
  const subWrapperClass = cx({
    [styles["with-icon"]]:
      Boolean(icon) || (showPasswordButton && type === "password"),
  });
  const iconWrapperClass = cx(styles["icon-wrapper"], {
    [styles["icon-left"]]: Boolean(icon) && iconPosition === "left",
  });
  const rawInputClass = cx(className, {
    [styles["with-icon--left"]]: icon && iconPosition === "left",
    [styles["with-icon--right"]]:
      (icon && iconPosition === "right") ||
      (showPasswordButton && type === "password"),
  });

  const effectiveType =
    showPasswordButton && type === "password" && isPasswordVisible
      ? "text"
      : type;

  return (
    <ControlWrapper
      tooltip={tooltip}
      numeric={numeric}
      numericText={numericText}
      disabled={disabled}
      maxLengthAbove={maxLengthAbove}
      error={error}
      helperText={helperText}
      inputId={id}
      fullWidth={fullWidth}
      value={value}
      label={
        autoSave ? (
          saved ? (
            <span className={cx([styles.bubble, styles.bubbleBackground])}>
              {savedLabel}
            </span>
          ) : (
            <span className={styles.bubble}></span>
          )
        ) : (
          label
        )
      }
      maxLengthText={maxLengthText}
      required={required}
      optionalLabelText={optionalLabelText}
      className={className}
      {...inputProps}
      renderControl={() => (
        <div className={subWrapperClass}>
          {icon && (
            <div className={iconWrapperClass}>
              <Icon
                icon={icon}
                size={iconSize}
                className={styles["icon-button"]}
                color={iconColor}
              />
            </div>
          )}
          <RawInput
            className={rawInputClass}
            disabled={disabled}
            error={error}
            id={id}
            required={required}
            onBlur={onBlur}
            setSaved={setSaved}
            rightSideText={rightSideText}
            type={effectiveType}
            onChange={onChange as (value: string | number) => void}
            value={value}
            {...(type === "number" && {
              onWheel: (event) => event.currentTarget.blur(),
            })}
            {...inputProps}
          />
          {showPasswordButton && type === "password" && (
            <Button
              className={cx(
                styles["icon-wrapper"],
                styles["password-toggle-button"]
              )}
              type="button"
              onClick={setStateValue(setIsPasswordVisible, !isPasswordVisible)}
              tabIndex={-1}
              data-testid="password-toggle-button"
              aria-label={isPasswordVisible ? "Hide password" : "Show password"}
              variant="transparent"
            >
              {isPasswordVisible
                ? coalesce(passwordToggleLabels?.hide, "hide")
                : coalesce(passwordToggleLabels?.show, "show")}
            </Button>
          )}
        </div>
      )}
    />
  );
}

type UncontrolledInputPropsBase = BaseInputProps & {
  /**
   * Debounce interval in milliseconds
   * @default 200
   */
  debounceInterval?: number;
};

type UncontrolledTextInputProps = UncontrolledInputPropsBase & {
  type?: "text" | "tel" | undefined;
  value?: string;
  defaultValue?: string;
  onChange: (value: string) => void;
};

type UncontrolledPasswordInputProps = UncontrolledInputPropsBase & {
  type: "password";
  value?: string;
  defaultValue?: string;
  onChange: (value: string) => void;
};

type UncontrolledNumberInputProps = UncontrolledInputPropsBase & {
  type: "number";
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  removeNonNumCharacters?: boolean;
  onChange: (value: number) => void;
};

type UncontrolledInputProps =
  | UncontrolledTextInputProps
  | UncontrolledPasswordInputProps
  | UncontrolledNumberInputProps;

/**
 * Uncontrolled version of the Input component with debounce functionality
 */
export function UncontrolledInput({
  debounceInterval = 200,
  value: initialValue,
  onChange,
  type,
  ...inputProps
}: UncontrolledInputProps): JSX.Element {
  const [internalValue, setInternalValue] = React.useState<string | number>(
    () => {
      if (type === "number") {
        return coalesce(initialValue, 0);
      }
      return coalesce(initialValue, "");
    }
  );
  const onChangeRef = React.useRef(onChange);

  React.useEffect(() => {
    if (initialValue === internalValue || (!initialValue && !internalValue)) {
      return;
    }
    const timeout = setTimeout(() => {
      (onChangeRef.current as Function)(internalValue);
    }, debounceInterval);

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [internalValue, debounceInterval, initialValue]);

  const handleChange = React.useCallback((newValue: typeof internalValue) => {
    setInternalValue(newValue);
  }, []);

  if (type === "number") {
    inputProps.onWheel = (event) => event.currentTarget.blur();
    return (
      <Input
        {...(inputProps as NumberInputProps)}
        type="number"
        value={internalValue as number}
        onChange={handleChange}
      />
    );
  }

  return (
    <Input
      {...(inputProps as TextInputProps)}
      type={type}
      value={internalValue as string}
      onChange={handleChange}
    />
  );
}
